import { DossierSettingsDto, SectionDto } from "@/shared/types";

export type DossierSettings = {
    enabledSections: Section[];
};

export const DossierSettings = {
    fromDto(dto: DossierSettingsDto): DossierSettings {
        return {
            enabledSections: dto.enabledSections.map((sectionDto) => Section.fromDto(sectionDto)),
        };
    },
    toDto(dto: DossierSettings): DossierSettingsDto {
        return {
            enabledSections: dto.enabledSections.map((sectionDto) => Section.toDto(sectionDto)),
        };
    },
    /**
     * Returns whether a section is visible in DossierDetails.
     *
     * Visibility can be toggled by users, unless the section is in use.
     * The enabled state is shared between users.
     */
    isSectionEnabled(settings: DossierSettings, sectionName: SectionName) {
        return settings.enabledSections.some((section) => section.name === sectionName);
    },
    /**
     * Returns whether a section currently contains data (content or messages).
     *
     * A section that is in use is automatically enabled in DossierDetails
     * and cannot be turned off by users.
     */
    isSectionInUse(settings: DossierSettings, sectionName: SectionName) {
        return settings.enabledSections.some((section) => section.name === sectionName && section.hasData);
    },

    getEnabledSectionNames(settings: DossierSettings) {
        return settings.enabledSections.map((enabledSection) => enabledSection.name);
    },

    areSectionNamesDifferent(sectionNamesA: SectionName[], sectionNamesB: SectionName[]) {
        const setA = new Set(sectionNamesA);
        const setB = new Set(sectionNamesB);
        return setA.symmetricDifference(setB).size > 0;
    },
    /**
     * Returns whether the enabled section names differ between two settings.
     *
     * Note: Only compares the set of enabled section names, not the hasData property.
     */
    areSectionsDifferent(settingsA: DossierSettings, settingsB: DossierSettings) {
        return this.areSectionNamesDifferent(this.getEnabledSectionNames(settingsA), this.getEnabledSectionNames(settingsB));
    },
};

export type SectionName =
    | "DossierDetails"
    | "VsmOfOpstartplan"
    | "Faciliteiten"
    | "FaciliteitenLandelijk"
    | "Tvv"
    | "BetrokkenTreinen"
    | "MedewerkersOpStation"
    | "Evaluatie";

export type Section = {
    name: SectionName;
    hasData: boolean;
};

const Section = {
    fromDto(dto: SectionDto): Section {
        return {
            name: dto.name,
            hasData: dto.hasData,
        };
    },
    toDto(dto: Section): SectionDto {
        return {
            name: dto.name,
            hasData: dto.hasData,
        };
    },
};
